import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../../../contexts/WeekIndexProvider";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { getVisibleWeekOffset, getWeekStart } from "../../../../utils/dateUtils";
import type { Nurse } from "../../../../utils/models";
import { FirstShiftDialog } from "./FirstShiftDialog";
import { VirtualizedWeekScheduleGrid } from "./VirtualizedWeekScheduleGrid";
import type { WeekMeta } from "./weekScheduleCellResolver";

type Props = {
	nurse: Nurse;
};

const UPCOMING_WEEKS = 8;

export const WeeklySchedule: FC<Props> = ({ nurse }) => {
	const { hospitalData, getNurseShifts, isAnyDemo, getEarliestShiftDate, isMedicalDepartment, timezone } = useHospitalData();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const { viewStartDay } = useWeekIndex();
	const [showFirstShiftDialog, setShowFirstShiftDialog] = useState(false);

	const offset = isDynamicWeekStartEnabled ? getVisibleWeekOffset(viewStartDay) : 0;

	const toWeekStart = useCallback(
		(date: DateTime): DateTime => (isDynamicWeekStartEnabled ? getWeekStart(date, viewStartDay) : date.startOf("day").startOf("week")),
		[isDynamicWeekStartEnabled, viewStartDay],
	);

	const startOfCurrentWeek = useMemo(() => toWeekStart(DateTime.now().setZone(timezone)), [timezone, toWeekStart]);

	const earliestShiftDate = useMemo(() => getEarliestShiftDate(), [getEarliestShiftDate]);

	const earliestWeekStart = useMemo(() => {
		if (!earliestShiftDate) return null;
		return toWeekStart(earliestShiftDate.setZone(timezone));
	}, [earliestShiftDate, timezone, toWeekStart]);

	const historicalWeeksCount = useMemo(() => {
		if (!earliestWeekStart) return 0;
		const diff = startOfCurrentWeek.diff(earliestWeekStart, "weeks").weeks;
		return Math.max(0, Math.floor(diff));
	}, [earliestWeekStart, startOfCurrentWeek]);

	const absoluteIndexBySundayStr = useMemo(() => {
		const map = new Map<string, number>();
		hospitalData.weeks.forEach((week, index) => {
			const firstDay = week.weeklyShifts.find((shift) => shift.days.length > 0)?.days[0];
			if (!firstDay) return;
			// days[0].date is the Sunday of the data-week; normalize to the Sunday-aligned key.
			const sundayKey = getWeekStart(firstDay.date.setZone(timezone), 0).toFormat("yyyy-MM-dd");
			map.set(sundayKey, index);
		});
		return map;
	}, [hospitalData.weeks, timezone]);

	// The contiguous data-week index that contains the given visible week-start.
	const resolveAbsoluteWeekIndex = useCallback(
		(weekStart: DateTime): number => {
			const sundayKey = getWeekStart(weekStart, 0).toFormat("yyyy-MM-dd");
			return absoluteIndexBySundayStr.get(sundayKey) ?? -1;
		},
		[absoluteIndexBySundayStr],
	);

	const weeks = useMemo(() => {
		const result: WeekMeta[] = [];
		if (earliestWeekStart) {
			for (let i = 0; i < historicalWeeksCount; i++) {
				const weekStart = earliestWeekStart.plus({ weeks: i });
				result.push({ weekStart, viewOnly: true, absoluteWeekIndex: resolveAbsoluteWeekIndex(weekStart) });
			}
		}
		for (let i = 0; i < UPCOMING_WEEKS; i++) {
			const weekStart = startOfCurrentWeek.plus({ weeks: i });
			result.push({ weekStart, viewOnly: isMedicalDepartment, absoluteWeekIndex: resolveAbsoluteWeekIndex(weekStart) });
		}
		return result;
	}, [earliestWeekStart, historicalWeeksCount, startOfCurrentWeek, isMedicalDepartment, resolveAbsoluteWeekIndex]);

	const handleDemoFirstShiftCreated = useCallback(() => {
		if (!isAnyDemo || !nurse.id || !nurse.createdAt) return;
		const startOfWeek = toWeekStart(DateTime.now());
		const dateToUse = startOfWeek < nurse.createdAt ? startOfWeek : nurse.createdAt;
		const shiftsAmountSinceCreated = getNurseShifts(nurse.id, dateToUse);
		if (shiftsAmountSinceCreated === 0) {
			setShowFirstShiftDialog(true);
		}
	}, [isAnyDemo, getNurseShifts, nurse.id, nurse.createdAt, toWeekStart]);

	const handleCloseDemoDialog = useCallback(() => {
		setShowFirstShiftDialog(false);
	}, []);

	return (
		<Box data-testid="profile-weekly-schedule" style={{ width: "100%", position: "relative" }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
			<VirtualizedWeekScheduleGrid
				weeks={weeks}
				weeksData={hospitalData.weeks}
				nurseFromProfile={nurse}
				onCreatedSingleShift={handleDemoFirstShiftCreated}
				offset={offset}
				showWeekStartDaySelector={true}
				showExportButton={true}
			/>
			{showFirstShiftDialog && <FirstShiftDialog open={showFirstShiftDialog} onClose={handleCloseDemoDialog} nurse={nurse} />}
		</Box>
	);
};
